import { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  AlertTriangle,
  Package,
  Users,
  CreditCard,
  Banknote,
  Smartphone,
  Clock,
  AlertCircle,
  CalendarClock,
  PackageX,
  RefreshCw,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { GlassCard } from '@/components/ui/glass-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DashboardMetrics {
  sales_today: {
    total: number;
    count: number;
    ticket_medio: number;
    variation_percent: number;
  };
  fiado_overdue: { total: number; customers: number };
  stock: { low_count: number; out_of_stock: number };
  registers: {
    open: number;
    details: Array<{ id: string; operator_id: string; initial_amount: number; opened_at: string }>;
  };
}

interface RecentSale {
  id: string;
  sale_number: number;
  total: number;
  time: string;
  payment_method: string;
}

interface WeeklyData {
  date: string;
  total: number;
  count: number;
}

interface AlertItem {
  type: string;
  severity: 'critical' | 'warning';
  message: string;
}

interface AlertsResponse {
  overdue_debts: AlertItem[];
  expiring_products: AlertItem[];
  low_stock: AlertItem[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PAYMENT_ICONS: Record<string, typeof DollarSign> = {
  dinheiro: Banknote,
  pix: Smartphone,
  cartao_debito: CreditCard,
  cartao_credito: CreditCard,
  fiado: Users,
  credito: Users,
};

const PAYMENT_LABELS: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'PIX',
  cartao_debito: 'Débito',
  cartao_credito: 'Crédito',
  fiado: 'Crédito',
  credito: 'Crédito',
  unknown: '—',
};

const ALERT_ICONS: Record<string, typeof AlertTriangle> = {
  fiado_vencido: AlertCircle,
  validade_proxima: CalendarClock,
  estoque_baixo: PackageX,
};

const ALERT_LABELS: Record<string, string> = {
  fiado_vencido: 'Crédito Vencido',
  validade_proxima: 'Validade Próxima',
  estoque_baixo: 'Estoque Baixo',
};

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Bom dia';
  if (hour < 18) return 'Boa tarde';
  return 'Boa noite';
}

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(dateStr: string): string {
  const [, month, day] = dateStr.split('-');
  return `${day}/${month}`;
}

function formatFullDate(): string {
  return new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function getDayName(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return DAY_NAMES[date.getDay()] || '';
}


// ─── Metric Card ─────────────────────────────────────────────────────────────

function MetricCard({
  title,
  value,
  subtitle,
  variation,
  icon: Icon,
  iconBg,
  className,
}: {
  title: string;
  value: string;
  subtitle?: string;
  variation?: number;
  icon: typeof DollarSign;
  iconBg: string;
  className?: string;
}) {
  return (
    <GlassCard className={`p-5 ${className || ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="mt-1.5 text-2xl font-bold text-foreground">{value}</p>
          {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        <div className={`rounded-xl p-3 ${iconBg}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
      {variation !== undefined && (
        <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-accent/30 px-2.5 py-1.5 w-fit">
          {variation >= 0 ? (
            <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
          ) : (
            <TrendingDown className="h-3.5 w-3.5 text-red-500" />
          )}
          <span className={`text-sm font-medium ${variation >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
            {variation >= 0 ? '+' : ''}{variation.toFixed(1)}%
          </span>
          <span className="text-xs text-muted-foreground">vs ontem</span>
        </div>
      )}
    </GlassCard>
  );
}

function MetricCardSkeleton() {
  return (
    <GlassCard className="p-5">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <Skeleton variant="text" width={80} height={14} />
          <Skeleton variant="text" width={120} height={28} />
          <Skeleton variant="text" width={60} height={14} />
        </div>
        <Skeleton variant="rectangular" width={44} height={44} />
      </div>
    </GlassCard>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function DashboardPage() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [recentSales, setRecentSales] = useState<RecentSale[]>([]);
  const [weeklyTrend, setWeeklyTrend] = useState<WeeklyData[]>([]);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  async function loadDashboard(showRefresh = false) {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [metricsRes, salesRes, trendRes, alertsRes] = await Promise.allSettled([
        apiFetch('/api/dashboard/metrics'),
        apiFetch('/api/dashboard/recent-sales'),
        apiFetch('/api/dashboard/weekly-trend'),
        apiFetch('/api/dashboard/alerts'),
      ]);

      if (metricsRes.status === 'fulfilled' && metricsRes.value.ok) {
        setMetrics(await metricsRes.value.json());
      }

      if (salesRes.status === 'fulfilled' && salesRes.value.ok) {
        const salesData = await salesRes.value.json();
        setRecentSales(salesData.data || []);
      }

      if (trendRes.status === 'fulfilled' && trendRes.value.ok) {
        const trendData = await trendRes.value.json();
        setWeeklyTrend(trendData.data || []);
      }

      if (alertsRes.status === 'fulfilled' && alertsRes.value.ok) {
        const alertsData: AlertsResponse = await alertsRes.value.json();
        const allAlerts: AlertItem[] = [
          ...(alertsData.overdue_debts || []),
          ...(alertsData.expiring_products || []),
          ...(alertsData.low_stock || []),
        ];
        setAlerts(allAlerts);
      }

      setLastUpdate(new Date());
    } catch {
      if (!showRefresh) toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadDashboard();
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => loadDashboard(true), 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <Skeleton variant="text" width={250} height={32} />
          <Skeleton variant="text" width={320} height={18} className="mt-2" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCardSkeleton />
          <MetricCardSkeleton />
          <MetricCardSkeleton />
          <MetricCardSkeleton />
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <GlassCard className="lg:col-span-2 p-5">
            <Skeleton variant="text" width={140} height={18} />
            <Skeleton variant="rectangular" width="100%" height={280} className="mt-4" />
          </GlassCard>
          <GlassCard className="p-5">
            <Skeleton variant="text" width={80} height={18} />
            <div className="mt-4 space-y-3">
              <Skeleton variant="text" width="100%" height={24} />
              <Skeleton variant="text" width="100%" height={24} />
              <Skeleton variant="text" width="100%" height={24} />
            </div>
          </GlassCard>
        </div>
      </div>
    );
  }

  const criticalAlerts = alerts.filter((a) => a.severity === 'critical');
  const warningAlerts = alerts.filter((a) => a.severity === 'warning');

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {getGreeting()}, {user?.name?.split(' ')[0] || 'Usuário'}
          </h1>
          <p className="mt-1 text-base text-muted-foreground capitalize">
            {formatFullDate()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground hidden sm:block">
            Atualizado às {lastUpdate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadDashboard(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Critical Alerts Banner */}
      {criticalAlerts.length > 0 && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <span className="text-base font-semibold text-red-400">
              {criticalAlerts.length} alerta{criticalAlerts.length > 1 ? 's' : ''} crítico{criticalAlerts.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-1">
            {criticalAlerts.slice(0, 3).map((alert, i) => (
              <p key={i} className="text-sm text-red-300">• {alert.message}</p>
            ))}
            {criticalAlerts.length > 3 && (
              <p className="text-sm text-red-300/70">e mais {criticalAlerts.length - 3}...</p>
            )}
          </div>
        </div>
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Vendas Hoje"
          value={formatCurrency(metrics?.sales_today.total || 0)}
          subtitle={metrics?.sales_today.count ? `${metrics.sales_today.count} venda${metrics.sales_today.count > 1 ? 's' : ''}` : 'Nenhuma venda'}
          variation={metrics?.sales_today.variation_percent}
          icon={ShoppingCart}
          iconBg="bg-primary/10 text-primary"
        />
        <MetricCard
          title="Ticket Médio"
          value={formatCurrency(metrics?.sales_today.ticket_medio || 0)}
          subtitle="Valor médio por venda"
          icon={DollarSign}
          iconBg="bg-cyan-500/10 text-cyan-500"
        />
        <MetricCard
          title="Crédito Vencido"
          value={formatCurrency(metrics?.fiado_overdue.total || 0)}
          subtitle={metrics?.fiado_overdue.customers ? `${metrics.fiado_overdue.customers} cliente${metrics.fiado_overdue.customers > 1 ? 's' : ''}` : 'Nenhum vencido'}
          icon={AlertTriangle}
          iconBg="bg-red-500/10 text-red-500"
        />
        <MetricCard
          title="Estoque Baixo"
          value={`${(metrics?.stock.low_count || 0) + (metrics?.stock.out_of_stock || 0)} itens`}
          subtitle={metrics?.stock.out_of_stock ? `${metrics.stock.out_of_stock} esgotado${metrics.stock.out_of_stock > 1 ? 's' : ''}` : 'Tudo em ordem'}
          icon={Package}
          iconBg="bg-amber-500/10 text-amber-500"
        />
      </div>


      {/* Chart + Alerts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Weekly Trend Chart */}
        <GlassCard className="lg:col-span-2 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-foreground">Vendas — Últimos 7 dias</h3>
            {weeklyTrend.length > 0 && (
              <span className="text-sm text-muted-foreground">
                Total: {formatCurrency(weeklyTrend.reduce((sum, d) => sum + d.total, 0))}
              </span>
            )}
          </div>
          {weeklyTrend.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={weeklyTrend.map((d) => ({ ...d, label: `${getDayName(d.date)} ${formatDate(d.date)}` }))}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="label"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(v: number) => v >= 1000 ? `R$${(v / 1000).toFixed(1)}k` : `R$${v}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '12px',
                      padding: '12px',
                    }}
                    formatter={(value: number, name: string) => {
                      if (name === 'total') return [formatCurrency(value), 'Vendas'];
                      return [value, name];
                    }}
                    labelFormatter={(label) => `📅 ${label}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="#10B981"
                    fill="url(#salesGradient)"
                    strokeWidth={2.5}
                    dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: '#10B981', stroke: '#fff', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex h-72 flex-col items-center justify-center gap-2">
              <ShoppingCart className="h-10 w-10 text-muted-foreground/30" />
              <p className="text-base text-muted-foreground">Nenhuma venda nos últimos 7 dias</p>
            </div>
          )}
        </GlassCard>

        {/* Alerts Panel */}
        <GlassCard className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-foreground">Alertas</h3>
            {alerts.length > 0 && (
              <span className="flex items-center justify-center h-6 min-w-[24px] rounded-full bg-amber-500/20 px-2 text-xs font-bold text-amber-400">
                {alerts.length}
              </span>
            )}
          </div>
          {alerts.length > 0 ? (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {alerts.map((alert, i) => {
                const AlertIcon = ALERT_ICONS[alert.type] || AlertTriangle;
                return (
                  <div
                    key={i}
                    className={`flex items-start gap-3 rounded-lg p-3 border transition-colors ${
                      alert.severity === 'critical'
                        ? 'border-red-500/20 bg-red-500/5 hover:bg-red-500/10'
                        : 'border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10'
                    }`}
                  >
                    <AlertIcon
                      className={`h-4 w-4 mt-0.5 shrink-0 ${
                        alert.severity === 'critical' ? 'text-red-400' : 'text-amber-400'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {ALERT_LABELS[alert.type] || alert.type}
                      </p>
                      <p className="text-sm text-foreground mt-0.5 truncate">{alert.message}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex h-48 flex-col items-center justify-center gap-2">
              <div className="rounded-full bg-green-500/10 p-3">
                <Package className="h-6 w-6 text-green-500" />
              </div>
              <p className="text-base text-muted-foreground">Tudo em ordem!</p>
              <p className="text-sm text-muted-foreground/70">Nenhum alerta no momento</p>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Recent Sales + Registers Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Sales */}
        <GlassCard className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-foreground">Últimas Vendas</h3>
            {recentSales.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {recentSales.length} mais recentes
              </span>
            )}
          </div>
          {recentSales.length > 0 ? (
            <div className="space-y-1">
              {recentSales.map((sale) => {
                const PayIcon = PAYMENT_ICONS[sale.payment_method] || DollarSign;
                const methodLabel = PAYMENT_LABELS[sale.payment_method] || sale.payment_method;
                return (
                  <div
                    key={sale.id}
                    className="flex items-center justify-between rounded-lg p-3 hover:bg-accent/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-primary/10">
                        <PayIcon className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-base font-medium text-foreground">
                          {formatCurrency(sale.total)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {methodLabel}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {sale.time}
                      </p>
                      <p className="text-xs text-muted-foreground/70">
                        #{sale.sale_number}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex h-48 flex-col items-center justify-center gap-2">
              <ShoppingCart className="h-8 w-8 text-muted-foreground/30" />
              <p className="text-base text-muted-foreground">Nenhuma venda hoje</p>
            </div>
          )}
        </GlassCard>

        {/* Register Status */}
        <GlassCard className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-foreground">Status dos Caixas</h3>
            {metrics && (
              <span className={`text-sm font-medium ${metrics.registers.open > 0 ? 'text-green-400' : 'text-muted-foreground'}`}>
                {metrics.registers.open > 0 ? `${metrics.registers.open} aberto${metrics.registers.open > 1 ? 's' : ''}` : 'Todos fechados'}
              </span>
            )}
          </div>
          {metrics && metrics.registers.open > 0 ? (
            <div className="space-y-3">
              {metrics.registers.details.map((reg, i) => {
                const openedAt = new Date(reg.opened_at);
                const hoursOpen = Math.floor((Date.now() - openedAt.getTime()) / 3600000);
                return (
                  <div key={reg.id} className="flex items-center justify-between rounded-xl border border-border p-4 bg-card/50">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="h-3 w-3 rounded-full bg-green-500" />
                        <div className="absolute inset-0 h-3 w-3 rounded-full bg-green-500 animate-ping opacity-30" />
                      </div>
                      <div>
                        <span className="text-base font-medium text-foreground">Caixa #{i + 1}</span>
                        <p className="text-xs text-muted-foreground">
                          Aberto há {hoursOpen > 0 ? `${hoursOpen}h` : 'menos de 1h'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Fundo</p>
                      <p className="text-base font-medium text-foreground">
                        {formatCurrency(Number(reg.initial_amount))}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex h-48 flex-col items-center justify-center gap-2">
              <div className="rounded-full bg-muted/50 p-3">
                <DollarSign className="h-6 w-6 text-muted-foreground/50" />
              </div>
              <p className="text-base text-muted-foreground">Nenhum caixa aberto</p>
              <p className="text-sm text-muted-foreground/70">Abra um caixa no PDV para começar</p>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Vendas por dia da semana (mini bar chart) */}
      {weeklyTrend.length > 0 && (
        <GlassCard className="p-5">
          <h3 className="text-base font-semibold text-foreground mb-4">Quantidade de Vendas por Dia</h3>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyTrend.map((d) => ({ ...d, day: getDayName(d.date) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [`${value} vendas`, 'Quantidade']}
                />
                <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} name="Vendas" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      )}
    </div>
  );
}
